import React, { Fragment, useCallback, useContext, useState } from 'react';
import Theme from '../../Theme';
import {
  StyledPlanHeader,
  StyledPlanRow,
  StyledPlanColumn,
  StyledFeatureSection,
  StyledBlueGradient,
  StyledFootnote,
} from './styles';

import CheckIcon from '../../SVG/CheckIcon';
import XIcon from '../../SVG/XIcon';
import Modal from '../Modal';
import Tooltip from '../Tooltip';

import {
  MC_PLAN_COMPARISON,
  EI_PLAN_COMPARISON,
} from '../../../constants/pricing/planComparison';
import { MC_PLANS, EI_PLANS } from '../../../constants/pricing/plans';
import PricingContext from '../../../context/pricing';

function PlanComparison() {
  const { state } = useContext(PricingContext);
  const [isOpen, toggleModal] = useState(false);
  
  const features =
    state.activeTab === 'mc' ? MC_PLAN_COMPARISON : EI_PLAN_COMPARISON;
  const plans = state.activeTab === 'mc' ? MC_PLANS : EI_PLANS;
  const cacheBuster =
    process.env.NODE_ENV === 'production' ? new Date().getTime() : '';
  /**
   * Check if the value is a string.
   * If it is, just return the value.
   * Otherwise, return a '✓' or 'x'.
   */
  const printPlanBox = useCallback((val) => {
    const { colorGreenLight } = Theme.color;
    const { colorPinkDark } = Theme.color;
    const { scaleBase } = Theme.scale;

    switch (typeof val) {
      case 'string': {
        const parsedVal = val.includes('\n')
          ? val.split('\n').map((item) => (
              <Fragment key={item}>
                {item}
                <br />
              </Fragment>
            ))
          : val;
        return <small>{parsedVal}</small>;
      }
      case 'object':
        return val.isChecked === true ? (
          <div className="with-footnote">
            <CheckIcon
              title="Included"
              size={scaleBase}
              color={colorGreenLight}
            />
            <sup>{val.symbol}</sup>
          </div>
        ) : (
          <div className="with-footnote">
            <XIcon
              title="Not included"
              size={scaleBase}
              color={colorPinkDark}
            />
            <sup>{val.symbol}</sup>
          </div>
        );

      default:
        return val === true ? (
          <CheckIcon
            title="Included"
            size={scaleBase}
            color={colorGreenLight}
            className="check-icon"
          />
        ) : (
          <XIcon title="Not included" size={scaleBase} color={colorPinkDark} />
        );
    }
  }, []);

  const printPlanSubHeading = useCallback((val) => {
    if (typeof val === 'string') {
      return val;
    }
    if (val === 0) {
      return '$0/mo';
    }
    return `starting at $${val.toLocaleString()}/mo`;
  }, []);

  const printColumnHeadings = useCallback(
    () =>
      Object.values(plans).map((plan) => (
        <StyledPlanColumn key={plan.name} modalIsOpen={isOpen} className="text-center">
          <h4>{plan.name}</h4>
          <i>{printPlanSubHeading(plan.startsAt)}</i>
        </StyledPlanColumn>
      )),
    [plans, isOpen]
  );

  const printFeatures = useCallback(
    (count) =>
      (count ? features.slice(0, count) : features).map((feature) => (
        <StyledFeatureSection key={feature.featureTitle}>
          <StyledPlanRow modalIsOpen={isOpen}>
            <p>
              <strong>{feature.featureTitle}</strong>
            </p>
          </StyledPlanRow>
          {feature.subFeatures.map(({ name, tooltip, ...featurePlans }) => (
            <StyledPlanRow key={name}>
              <StyledPlanColumn>
                {tooltip ? <Tooltip tip={tooltip}>{name}</Tooltip> : name}
              </StyledPlanColumn>
              {Object.entries(featurePlans).map(([key, val]) => (
                <StyledPlanColumn key={key} className="text-center">
                  {printPlanBox(val)}
                </StyledPlanColumn>
              ))}
            </StyledPlanRow>
          ))}
        </StyledFeatureSection>
      )),
    [features, isOpen]
  );

  return (
    <>
      <div className="m-bottom-10 p-bottom-10 bg-gray-900 d-none-tablet">
        <StyledPlanHeader className="row row--small m-bottom-8 text-center">
          <h5>FULL FEATURE BREAKDOWN</h5>
          <h2>Choose exactly what you need.</h2>
          <p>
            Compare the features and benefits of each plan. Still unsure which
            plan is best for you?{' '}
            <a href="/contact-us-form/">We&apos;re here to help</a>.
          </p>
        </StyledPlanHeader>
        <div className="p-relative">
          <div className="row row--large">
            <StyledPlanRow className="plan-row-header">
              <StyledPlanColumn>
                <h4 className="compare">Compare features by plan:</h4>
              </StyledPlanColumn>
              {printColumnHeadings()}
            </StyledPlanRow>
            {printFeatures(2)}
          </div>
          <StyledBlueGradient />
        </div>
        <div className="row row--large m-top-8">
          <button
            type="button"
            onClick={() => toggleModal(true)}
            title="See full feature breakdown"
            className="arrow-link reversed-button"
          >
            See Full Feature Breakdown
          </button>
        </div>
      </div>
      <Modal
        isWide
        isOpen={isOpen}
        close={toggleModal}
        contentLabel="Compare features"
        hasStickyHeader
        title="Full Plan Comparison"
        stickyContent={
          <StyledPlanRow className="plan-row-header">
            <StyledPlanColumn>
              <a
                href={
                  state.activeTab === 'e'
                    ? 'https://sendgrid.com/wp-content/uploads/pdf/Twilio-SendGrid-API-Plan-Comparison-061622.pdf'
                    : `/files/Twilio-SendGrid-Marketing-Campaigns-Plan-Comparison.pdf?ver=${cacheBuster}`
                }
                download
                className="arrow-link download"
              >
                Download PDF
              </a>
            </StyledPlanColumn>
            {printColumnHeadings()}
          </StyledPlanRow>
        }
      >
        <div className="plan-modal">
          {printFeatures()}
          {state.activeTab === 'e' && (
            <StyledFootnote>
              <p>* You can purchase additional IPs as an add-on</p>
              <p>
                ** Premier 100M and above do not currently have this feature
              </p>
              <p>*** Not available to Premier 100M+</p>
            </StyledFootnote>
          )}
        </div>
      </Modal>
    </>
  );
}

export default PlanComparison;
